import React from 'react';
import classNames from 'classnames';

import { Icon } from 'js/design_system/Icon';
import { trackClick, Options as TrackClickOptions } from 'js/utils/trackClick';

import 'app/assets/stylesheets/design_system/text_button.scss';

// Shared between this and TextButtonLink
export type BaseTextButtonProps = {
  size?: 'sm' | 'lg';
  inline?: boolean;
  inverse?: boolean;
  icon?: string | React.ReactNode;
  iconRight?: string | React.ReactNode;
  trackClick?: TrackClickOptions;
};

type TextButtonProps = BaseTextButtonProps &
  React.ComponentPropsWithoutRef<'button'>;

/**
 * A text-flavored button for handling click events or submitting forms.
 *
 * Props:
 * - `size`: The size of the button - one of 'sm' or 'lg' (default: 'lg')
 * - `inline`: Button without a set height or padding, to look and act like inline text (default: false)
 * - `inverse`: Button to be used on darker backgrounds (default: false)
 * - `icon`: The name of a Material Symbols icon (string) or an arbitrary React component to render as
 *     the button's icon (left of the text)
 * - `iconRight`: The name of a Material Symbols icon (string) or an arbitrary React component to render
 *     as the button's icon (right of the text)
 * - `trackClick`: Options for tracking clicks on this button
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { TextButton } from 'js/design_system/TextButton';
 *
 * <TextButton onClick={clickHandler}>
 *   Click me!
 * </TextButton>
 * ```
 *
 */
export const TextButton = React.forwardRef<HTMLButtonElement, TextButtonProps>(
  (
    {
      size = 'lg',
      inline = false,
      inverse = false,
      icon,
      iconRight,
      className,
      children,
      trackClick: trackClickOptions,
      onClick,
      ...restProps
    },
    ref,
  ) => {
    const classes = classNames('ds2-5-text-link', className, {
      'ds2-5-text-link--small': size === 'sm',
      'ds2-5-text-link--inline': inline,
      'ds2-5-text-link--inverse': inverse,
    });

    let iconView = null;
    if (icon) {
      iconView =
        typeof icon === 'string' ? <Icon name={icon} size={20} /> : icon;
    }

    let iconRightView = null;
    if (iconRight) {
      iconRightView =
        typeof iconRight === 'string' ? (
          <Icon name={iconRight} size={20} />
        ) : (
          iconRight
        );
    }

    const handleClick = trackClickOptions
      ? (event: React.MouseEvent<HTMLButtonElement>) => {
          if (trackClickOptions) {
            trackClick(trackClickOptions);
          }
          return onClick?.(event);
        }
      : onClick;

    return (
      <button
        className={classes}
        {...restProps}
        onClick={handleClick}
        ref={ref}
      >
        {iconView ? iconView : null}
        {children && (
          <span className="ds2-5-text-link__content">{children}</span>
        )}
        {iconRightView ? iconRightView : null}
      </button>
    );
  },
);
